'use client';

import { useState, useMemo, type ReactNode } from 'react';
import type {
  SourceConfig,
  SourceConfigUpdate,
  DigestSchedule,
  TriageDestination
} from 'dico-types';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { MonoIcon } from '@/components/ui/MonoIcon';
import type { MonoIconType } from '@/components/ui/MonoIcon';
import { getDiscordChannelIcon } from '@/lib/channelIcons';
import { Switch } from '@/components/ui/switch';
import type { SlackChannel, DiscordChannel } from '@/lib/api/integration/integrationApi';
import Loader from '../ui/loader';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ButtonGroup } from '../ui/button-group';

interface AvailableTriageDestination {
  channelId: string;
  channelName: string;
  platform: 'slack' | 'discord';
  integrationId: string;
  accountName: string;
}

interface SourceConfigRowProps {
  source: SourceConfig;
  workspaceId: string;
  accountId: string;
  provider: string;
  availableChannels?: SlackChannel[];
  availableDiscordChannels?: DiscordChannel[];
  availableTriageDestinations?: AvailableTriageDestination[];
  onUpdate: (sourceId: string, patch: SourceConfigUpdate) => Promise<void>;
  onRemove: (sourceId: string) => Promise<void>;
  onBackfill?: (sourceId: string, daysBack: number) => Promise<void>;
  onChannelChange?: (newChannelId: string, newChannelName: string) => Promise<void>;
  onKBInit?: (sourceId: string) => Promise<void>;
  kbInitializing?: boolean;
}

const SCHEDULE_OPTIONS: { label: string; value: DigestSchedule }[] = [
  { label: 'Every 4 hours', value: { type: 'interval', hours: 4 } },
  { label: 'Every 8 hours', value: { type: 'interval', hours: 8 } },
  { label: 'Daily at 8am UTC', value: { type: 'daily', hour: 8, timezone: 'UTC' } },
  { label: 'Daily at 9am UTC', value: { type: 'daily', hour: 9, timezone: 'UTC' } }
];

const GITHUB_EVENT_OPTIONS = [
  { value: 'issues', label: 'Issues' },
  { value: 'discussions', label: 'Discussions' },
  { value: 'prs', label: 'Pull Requests' }
];

const SYNC_DAYS_ITEMS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' }
];

export function SourceConfigRow({
  source,
  workspaceId: _workspaceId, // eslint-disable-line @typescript-eslint/no-unused-vars -- part of API shape
  accountId: _accountId, // eslint-disable-line @typescript-eslint/no-unused-vars -- part of API shape
  provider,
  availableChannels = [],
  availableDiscordChannels = [],
  availableTriageDestinations = [],
  onUpdate,
  onRemove,
  onBackfill,
  onChannelChange,
  onKBInit,
  kbInitializing = false
}: SourceConfigRowProps) {
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [syncDaysBack, setSyncDaysBack] = useState(30);
  const [addPlatform, setAddPlatform] = useState<'slack' | 'discord' | ''>('');
  const [addChannelKey, setAddChannelKey] = useState<string>('');

  const triageDestinationKey = (dest: AvailableTriageDestination) =>
    `${dest.platform}:${dest.integrationId}:${dest.channelId}`;

  const handleModeToggle = async (mode: 'realtime' | 'digest') => {
    setSaving(true);
    try {
      const patch: SourceConfigUpdate = { ingestMode: mode };
      if (mode === 'realtime') patch.digestSchedule = null;
      else if (!source.digestSchedule) {
        patch.digestSchedule = { type: 'daily', hour: 8, timezone: 'UTC' };
      }
      await onUpdate(source.id, patch);
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleChange = async (value: string) => {
    setSaving(true);
    try {
      const opt = SCHEDULE_OPTIONS.find((o) => JSON.stringify(o.value) === value);
      if (!opt) return;
      await onUpdate(source.id, { ingestMode: 'digest', digestSchedule: opt.value });
    } finally {
      setSaving(false);
    }
  };

  const handleEventTypeToggle = async (eventType: string) => {
    const current = source.eventTypes ?? [];
    const next = current.includes(eventType)
      ? current.filter((t) => t !== eventType)
      : [...current, eventType];
    setSaving(true);
    try {
      await onUpdate(source.id, { eventTypes: next });
    } finally {
      setSaving(false);
    }
  };

  const handleNotifyChannelChange = async (channelId: string) => {
    await onUpdate(source.id, { notifyChannelId: channelId || null });
  };

  const handleAddTriageDestination = async (destination: TriageDestination) => {
    const current = source.triageDestinations ?? [];
    if (
      current.some(
        (d) => d.channelId === destination.channelId && d.platform === destination.platform
      )
    ) {
      // Avoid leaving the picker stuck on a duplicate value.
      setAddChannelKey('');
      setAddPlatform('');
      return;
    }
    const updated = [...current, destination];
    setSaving(true);
    try {
      await onUpdate(source.id, { triageDestinations: updated });
    } finally {
      setSaving(false);
      setAddChannelKey('');
      setAddPlatform('');
    }
  };

  const handleRemoveTriageDestination = async (index: number) => {
    const current = source.triageDestinations ?? [];
    const updated = current.filter((_, i) => i !== index);
    setSaving(true);
    try {
      await onUpdate(source.id, { triageDestinations: updated });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove(source.id);
    } finally {
      setRemoving(false);
    }
  };

  const handleBackfill = async () => {
    if (!onBackfill) return;
    setBackfilling(true);
    try {
      await onBackfill(source.id, syncDaysBack);
    } finally {
      setBackfilling(false);
    }
  };

  const handleKBInit = async () => {
    if (!onKBInit) return;
    try {
      await onKBInit(source.id);
    } catch (error) {
      // Error handling done in parent
    }
  };

  const handleChannelChange = async (newChannelId: string) => {
    if (!onChannelChange) return;
    const isSlackProvider = provider === 'slack';
    const ch = isSlackProvider
      ? availableChannels.find((c) => c.id === newChannelId)
      : availableDiscordChannels.find((c) => c.id === newChannelId);
    if (!ch) return;
    setSaving(true);
    try {
      await onChannelChange(newChannelId, ch.name);
    } finally {
      setSaving(false);
    }
  };

  const isDigest = source.ingestMode === 'digest';
  const currentScheduleValue = source.digestSchedule ? JSON.stringify(source.digestSchedule) : '';
  const currentScheduleLabel =
    SCHEDULE_OPTIONS.find((o) => JSON.stringify(o.value) === currentScheduleValue)?.label ?? '—';
  const isSlack = provider === 'slack';

  const currentDiscordChannel = !isSlack
    ? availableDiscordChannels.find((c) => c.id === source.sourceId)
    : undefined;
  const channelIconType: MonoIconType = isSlack
    ? 'Hash'
    : getDiscordChannelIcon(currentDiscordChannel?.type ?? 0);

  const getTriageDestinationChannelIcon = (dest: TriageDestination): MonoIconType => {
    if (dest.platform === 'slack') return 'Hash';
    const discordChannel = availableDiscordChannels.find((c) => c.id === dest.channelId);
    return getDiscordChannelIcon(discordChannel?.type ?? 0);
  };

  const slackTextChannels = availableChannels.filter((c) => !c.isArchived);
  const discordTextChannels = availableDiscordChannels.filter((c) => c.type === 0);
  const discordAnnouncementChannels = availableDiscordChannels.filter((c) => c.type === 5);
  const discordForumChannels = availableDiscordChannels.filter((c) => c.type === 15);

  const addPlatformDestinations = addPlatform
    ? availableTriageDestinations.filter((d) => d.platform === addPlatform)
    : [];
  const selectedAddDestination = addChannelKey
    ? availableTriageDestinations.find((d) => triageDestinationKey(d) === addChannelKey)
    : undefined;
  const availableAddPlatforms = Array.from(
    new Set(availableTriageDestinations.map((d) => d.platform))
  ) as Array<'slack' | 'discord'>;

  const notifyChannelItems = useMemo(() => {
    const record: Record<string, ReactNode> = {};
    for (const ch of availableChannels.filter((c) => !c.isArchived)) {
      record[ch.id] = (
        <div className="flex items-center gap-1.5">
          <MonoIcon type="Hash" className="text-muted-foreground" />
          <span>{ch.name}</span>
        </div>
      );
    }
    return record;
  }, [availableChannels]);

  return (
    <div
      className={`border-border bg-card shadow-xs flex flex-col rounded-md border text-sm transition-opacity ${!source.isActive ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between p-2">
        <div className="flex min-w-0 items-center gap-2">
          {source.sourceType === 'channel' && onChannelChange ? (
            <Select value={source.sourceId} onValueChange={handleChannelChange} disabled={saving}>
              <SelectTrigger>
                <SelectValue>
                  <div className="flex items-center gap-1.5">
                    <MonoIcon type={channelIconType} className="text-muted-foreground" />
                    <span className="truncate font-medium">{source.sourceName}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {isSlack ? (
                  slackTextChannels.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      <div className="flex items-center gap-1.5">
                        <MonoIcon type="Hash" className="text-muted-foreground" />
                        <span>{ch.name}</span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <>
                    {discordTextChannels.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Text Channels</SelectLabel>
                        {discordTextChannels.map((ch) => (
                          <SelectItem key={ch.id} value={ch.id}>
                            <div className="flex items-center gap-1.5">
                              <MonoIcon type="Hash" className="text-muted-foreground" />
                              <span>{ch.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {discordAnnouncementChannels.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Announcements</SelectLabel>
                        {discordAnnouncementChannels.map((ch) => (
                          <SelectItem key={ch.id} value={ch.id}>
                            <div className="flex items-center gap-1.5">
                              <MonoIcon type="Megaphone" className="text-muted-foreground" />
                              <span>{ch.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {discordForumChannels.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Forums</SelectLabel>
                        {discordForumChannels.map((ch) => (
                          <SelectItem key={ch.id} value={ch.id}>
                            <div className="flex items-center gap-1.5">
                              <MonoIcon type="ChatBubble" className="text-muted-foreground" />
                              <span>{ch.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </>
                )}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-foreground truncate font-medium">
              {source.sourceType === 'channel' ? '#' : ''}
              {source.sourceName}
            </span>
          )}

          <Switch
            checked={source.isActive}
            onCheckedChange={(checked) => onUpdate(source.id, { isActive: checked })}
            aria-label={source.isActive ? 'Disable' : 'Enable'}
            size="sm"
          />
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {onKBInit && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleKBInit}
              disabled={kbInitializing}
              tooltip={
                kbInitializing
                  ? 'KB initialization in progress'
                  : 'Initialize knowledge base from this source'
              }
            >
              {kbInitializing ? (
                <Loader className="mr-1" />
              ) : (
                <MonoIcon type="Brain" className="mr-1" />
              )}
              Init KB
            </Button>
          )}
          {onBackfill && (
            <ButtonGroup>
              <Select
                value={String(syncDaysBack)}
                onValueChange={(v) => setSyncDaysBack(Number(v))}
                disabled={backfilling}
                items={SYNC_DAYS_ITEMS}
              >
                <SelectTrigger size="sm" className="border-r-1 w-auto gap-1 rounded-r-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBackfill}
                className={cn('rounded-l-none', backfilling && 'opacity-60')}
                disabled={backfilling}
              >
                {backfilling ? (
                  <Loader className="mr-1" />
                ) : (
                  <MonoIcon type="RefreshCcw" className="mr-1" />
                )}
                Sync
              </Button>
            </ButtonGroup>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
              disabled={saving}
            >
              <span className="text-sm font-medium">{isDigest ? 'Digest' : 'Real-time'}</span>
              <MonoIcon type="ChevronDown" className="ml-1 h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleModeToggle('realtime')} disabled={saving}>
                {!isDigest && <MonoIcon type="Check" className="h-4 w-4" />}
                Real-time
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  onClick={() => {
                    if (!isDigest) handleModeToggle('digest');
                  }}
                  disabled={saving}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {isDigest && <MonoIcon type="Check" className="h-4 w-4 shrink-0" />}
                    <span className="truncate">Digest</span>
                    {isDigest && (
                      <span className="text-muted-foreground truncate text-xs">
                        {currentScheduleLabel}
                      </span>
                    )}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuGroup>
                    {SCHEDULE_OPTIONS.map((opt) => {
                      const optValue = JSON.stringify(opt.value);
                      const selected = optValue === currentScheduleValue;
                      return (
                        <DropdownMenuItem
                          key={optValue}
                          onClick={() => handleScheduleChange(optValue)}
                          disabled={saving}
                        >
                          {selected && <MonoIcon type="Check" className="h-4 w-4" />}
                          {opt.label}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(buttonVariants({ variant: 'outline', size: 'icon-sm' }))}
              disabled={removing}
            >
              <MonoIcon type="MoreHorizontal" className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem variant="destructive" onClick={handleRemove} disabled={removing}>
                <MonoIcon type="Trash" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* GitHub event types */}
      {source.sourceType === 'repo' && (
        <div className="flex items-start gap-2">
          <span className="text-muted-foreground w-20 shrink-0 pt-0.5">Events</span>
          <div className="flex flex-wrap gap-1.5">
            {GITHUB_EVENT_OPTIONS.map((opt) => {
              const active = source.eventTypes?.includes(opt.value);
              return (
                <Button
                  key={opt.value}
                  variant={active ? 'secondary' : 'ghost'}
                  size="xs"
                  disabled={saving}
                  onClick={() => handleEventTypeToggle(opt.value)}
                  className="rounded"
                >
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Triage destinations (multi-platform) */}
      <div className="border-border flex flex-col gap-2 border-t p-2">
        {source.notifyEnabled && (
          <>
            {/* Legacy Slack-only mode */}
            {isSlack && (!source.triageDestinations || source.triageDestinations.length === 0) && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20 shrink-0 text-xs">Channel</span>
                <Select
                  value={source.notifyChannelId ?? ''}
                  onValueChange={handleNotifyChannelChange}
                  items={notifyChannelItems}
                >
                  <SelectTrigger size="sm">
                    <SelectValue placeholder="— select channel —" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableChannels
                      .filter((c) => !c.isArchived)
                      .map((ch) => (
                        <SelectItem key={ch.id} value={ch.id}>
                          <div className="flex items-center gap-1.5">
                            <MonoIcon type="Hash" className="text-muted-foreground" />
                            <span>{ch.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Multi-destination mode */}
            {source.triageDestinations && source.triageDestinations.length > 0 && (
              <div className="flex flex-col gap-2">
                {source.triageDestinations.map((dest, idx) => {
                  const destInfo = availableTriageDestinations.find(
                    (d) => d.channelId === dest.channelId && d.platform === dest.platform
                  );
                  const accountLabel = destInfo?.accountName ?? 'Unknown';

                  return (
                    <div key={idx} className="bg-card flex items-center justify-between rounded-md">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2 px-1">
                          <MonoIcon
                            type={dest.platform === 'slack' ? 'Slack' : 'Discord'}
                            className="shrink-0"
                          />

                          <div className="flex items-center gap-0.5">
                            <MonoIcon
                              type={getTriageDestinationChannelIcon(dest)}
                              className="text-foreground"
                            />
                            <span className="text-sm font-medium capitalize tracking-tight">
                              {dest.channelName}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="icon-sm"
                        onClick={() => handleRemoveTriageDestination(idx)}
                        disabled={saving}
                        aria-label="Remove destination"
                      >
                        <MonoIcon type="Trash" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Inline destination picker (platform + channel) */}
            {availableTriageDestinations.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Select
                  value={addPlatform}
                  onValueChange={(v) => {
                    setAddPlatform(v as 'slack' | 'discord' | '');
                    setAddChannelKey('');
                  }}
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue>
                      {addPlatform ? (
                        <div className="flex items-center gap-1.5">
                          <MonoIcon
                            type={addPlatform === 'slack' ? 'Slack' : 'Discord'}
                            className="text-muted-foreground"
                          />
                          <span className="font-medium">
                            {addPlatform === 'slack' ? 'Slack' : 'Discord'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">— select platform —</span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">
                      <span className="text-muted-foreground">— select platform —</span>
                    </SelectItem>
                    {availableAddPlatforms.map((platform) => (
                      <SelectItem key={platform} value={platform}>
                        <div className="flex items-center gap-1.5">
                          <MonoIcon
                            type={platform === 'slack' ? 'Slack' : 'Discord'}
                            className="text-muted-foreground"
                          />
                          <span className="font-medium">
                            {platform === 'slack' ? 'Slack' : 'Discord'}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={addChannelKey}
                  onValueChange={(key) => {
                    setAddChannelKey(key);
                    const dest = availableTriageDestinations.find(
                      (d) => triageDestinationKey(d) === key
                    );
                    if (!dest) return;
                    void handleAddTriageDestination({
                      channelId: dest.channelId,
                      channelName: dest.channelName,
                      platform: dest.platform,
                      integrationId: dest.integrationId
                    });
                  }}
                  disabled={saving || !addPlatform}
                >
                  <SelectTrigger>
                    <SelectValue>
                      {selectedAddDestination ? (
                        <div className="flex items-center gap-1.5">
                          <MonoIcon
                            type={getTriageDestinationChannelIcon(selectedAddDestination)}
                            className="text-muted-foreground"
                          />
                          <span className="truncate font-medium">
                            {selectedAddDestination.channelName}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">— select channel —</span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(new Set(addPlatformDestinations.map((d) => d.accountName))).map(
                      (accountName) => {
                        const accountDestinations = addPlatformDestinations.filter(
                          (d) => d.accountName === accountName
                        );
                        return (
                          <SelectGroup key={accountName}>
                            <SelectLabel>{accountName}</SelectLabel>
                            {accountDestinations.map((dest) => {
                              const key = triageDestinationKey(dest);
                              return (
                                <SelectItem key={key} value={key}>
                                  <div className="flex items-center gap-1.5">
                                    <MonoIcon
                                      type={getTriageDestinationChannelIcon(dest)}
                                      className="text-muted-foreground"
                                    />
                                    <span className="font-medium">{dest.channelName}</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectGroup>
                        );
                      }
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
